import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TabsModule } from 'primeng/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProgressBarModule } from 'primeng/progressbar';
import isEqual from 'fast-deep-equal';
import { SharedService } from '@shared/services/shared.service';

export interface InfoItemOption {
  label: string;
  value: string | number | boolean | null | undefined;
}

export interface InfoItem {
  key?: string;
  label: string;
  value: string | number | boolean | null | undefined | string[];
  separatorBefore?: boolean;
  readonly?: boolean;
  isRequired?: boolean;

  isEditable?: boolean;
  options?: InfoItemOption[];
  isMultiSelect?: boolean;
  selectedValue?: string | string[] | number | number[] | boolean | boolean[] | null;
}

export interface InfoTab {
  value: string;
  label: string;
  content?: string;
}

@Component({
  selector: 'info-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DrawerModule,
    ButtonModule,
    SelectModule,
    MultiSelectModule,
    TranslateModule,
    TabsModule,
    ProgressBarModule 

  ],
  templateUrl: './info-drawer.component.html',
  styleUrls: ['./info-drawer.component.scss']
})
export class InfoDrawerComponent implements OnChanges {

  @Input() title = 'Information';

  @Input() buttonLabel = 'Info';

  @Input() buttonIcon = 'pi pi-info-circle';

  @Input() items: InfoItem[] = [];

  @Input() tabs: InfoTab[] = [];

  @Input() loading: boolean = false;

  @Input() discardingChangesMessage = 'translate.cat.discardEditChanges';

  @Input() buttonSize = 'small';
  @Input() saving = false;
  @Input() showEdit: boolean = true;

  @Output() itemChange = new EventEmitter<{ item: InfoItem; value: unknown }>();
  @Output() saveChanges = new EventEmitter<() => void>();
  @Output() discardChanges = new EventEmitter<void>();
  @Output() editModeChange = new EventEmitter<boolean>();
  @Output() showDrawer = new EventEmitter();

  editMode: boolean = false;
  hasChanges = false;
  isValid: boolean = false;
  private initialItems: InfoItem[] = [];

  visible = false;

  constructor(private sharedService: SharedService, private translateService: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && !this.editMode && !this.saving) {
      this.saveInitialState();
    }
  }

  open(): void {
    this.visible = true;
  }

  close(): void {
    this.requestClose();
  }

  onVisibleChange(visible: boolean): void {
    if (visible) {
      this.visible = true;
      return;
    }

    this.requestClose();
  }

  private requestClose(): void {
    if (this.editMode && this.hasChanges && !this.saving) {

      let validationStr = this.translateService.instant(this.discardingChangesMessage);
      this.sharedService.emitPopupchReceivedEvent({
        title: 'translate.validation.confirm',
        icon: 'exclamation-triangle',
        subTitle: validationStr,
        keep: false,
        approve: () => {
        this.onDiscardChanges();
        this.editMode = false;
        this.visible = false;
        this.editModeChange.emit(false);
        },
      });


    } else {
      if (this.editMode) {
        this.onDiscardChanges();
        this.editMode = false;
        this.editModeChange.emit(false);
      }
      this.visible = false;
    }

    
  }

  toggleEditMode(): void {
    if (this.saving || this.editMode) {
      return;
    }

    this.editMode = true;
    this.saveInitialState();
    this.editModeChange.emit(true);
  }

  displayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : '-';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  }

  getInputValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    return value === null || value === undefined ? '' : String(value);
  }

  onItemValueChange(item: InfoItem, value: unknown): void {
    if (this.saving) {
      return;
    }

    item.selectedValue = value as any;
    item.value = value as InfoItem['value'];
    this.hasChanges = !isEqual(this.items, this.initialItems);
    this.isValid = this.setValid();
    this.itemChange.emit({ item, value });
  }
  setValid(): boolean {
    if (this.items) {
      
      for (let item of this.items) {
        if (item.isRequired && item.value == null) {
          return false;
        }
      }
    }

    return true;
  }
  onSaveChanges(): void {
    this.saveChanges.emit(() => this.markSavedAndExitEditMode());
  }

  onDiscardChanges(): void {
    this.items.splice(0, this.items.length, ...structuredClone(this.initialItems));
    this.hasChanges = false;
    this.discardChanges.emit();
  }

  onCancelChanges(): void {
    this.onDiscardChanges();
    this.editMode = false;
    this.editModeChange.emit(false);
  }

  private saveInitialState(): void {
    this.initialItems = structuredClone(this.items);
    this.hasChanges = false;
  }

  private markSavedAndExitEditMode(): void {
    this.hasChanges = false;
    this.editMode = false;
    this.editModeChange.emit(false);
  }
  onShow() {
    this.loading = true;
    this.showDrawer.emit();
  }
}